// ChatAssistant.jsx
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import { SummarizeMessage } from "../../../Actions/Messaging";
import Spinner from "../../Animations/Spinner/Spinner";
import { X as CloseIcon } from "lucide-react";

class ChatAssistant extends Component {
  state = {
    start_date: null,
    end_date: null,
    isSummarizing: false,
  };

  static propTypes = {
    SummarizeMessage: PropTypes.func.isRequired,
    messageSummarySuccess: PropTypes.bool,
    summarizedMessageAndTimeline: PropTypes.object,
    phoneNumber: PropTypes.string.isRequired,
    closeChatAssistant: PropTypes.func.isRequired,
    // 1) We add a new prop seekChatRange, which we'll get from the parent
    seekChatRange: PropTypes.func,
  };

  onChange = (e) => this.setState({ [e.target.name]: e.target.value });

  handleSubmit = (e) => {
    e.preventDefault();
    const { phoneNumber, SummarizeMessage } = this.props;
    const { start_date, end_date } = this.state;

    if (!start_date || !end_date) {
      alert("Please select a valid date range.");
      return;
    }

    SummarizeMessage("+2348067390962", start_date, end_date);

    this.setState({ isSummarizing: true });
  };

  // 2) handleSeek method: dispatch or pass the timestamps to the parent
  handleSeek = () => {
    const { summarizedMessageAndTimeline, seekChatRange } = this.props;
    if (!summarizedMessageAndTimeline) {
      alert("No summary has been generated yet.");
      return;
    }

    const { start_timestamp, end_timestamp } = summarizedMessageAndTimeline;

    // If you want the parent to handle the logic, call the callback:
    if (seekChatRange) {
      seekChatRange(
        this.props.summarizedMessageAndTimeline?.start_timestamp,
        this.props.summarizedMessageAndTimeline?.end_timestamp
      );
    }
  };

  componentDidUpdate(prevProps) {
    const { summarizedMessageAndTimeline, messageSummaryError } = this.props;

    // Stop spinner if request is successful
    if (
      prevProps.summarizedMessageAndTimeline !== summarizedMessageAndTimeline &&
      summarizedMessageAndTimeline
    ) {
      this.setState({ isSummarizing: false });
    }

    // Stop spinner if an error occurs
    if (
      prevProps.messageSummaryError !== messageSummaryError &&
      messageSummaryError
    ) {
      this.setState({ isSummarizing: false });
    }
  }

  render() {
    const { closeChatAssistant } = this.props;
    const { start_date, end_date, isSummarizing } = this.state;

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={closeChatAssistant}
        />

        {/* Right drawer */}
        <div className="fixed top-0 right-0 h-screen w-[380px] bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10" style={{ background: "#0f1f3d" }}>
            <div className="font-SFPro font-bold text-lg text-white">
              CES Chat Assistant
            </div>
            <button
              onClick={closeChatAssistant}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <CloseIcon className="h-3 w-3 text-white" aria-hidden="true" />
              <span className="sr-only">Close</span>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
            {/* Date range section */}
            <div>
              <div className="font-SFPro font-semibold text-sm text-gray-900 mb-3">
                Select Date Range
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="font-inter text-xs font-medium text-gray-600">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={start_date || ""}
                    onChange={this.onChange}
                    className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-blue focus:border-primary-blue"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="font-inter text-xs font-medium text-gray-600">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={end_date || ""}
                    onChange={this.onChange}
                    className="w-full h-9 bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary-blue focus:border-primary-blue"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button
                onClick={this.handleSubmit}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary-blue hover:bg-blue-700 text-white font-inter font-semibold text-sm rounded-lg px-5 py-2.5 transition-colors"
              >
                <span>Summarize</span>
                {isSummarizing && <Spinner />}
              </button>
              <button
                onClick={this.handleSeek}
                className="w-full inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-inter font-semibold text-sm rounded-lg px-5 py-2.5 transition-colors"
              >
                Seek
              </button>
            </div>

            {/* Summary result */}
            {this.props.summarizedMessageAndTimeline?.summary && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 font-inter text-sm text-gray-900 leading-relaxed overflow-y-auto">
                <div
                  dangerouslySetInnerHTML={{
                    __html: this.props.summarizedMessageAndTimeline.summary,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  messageSummarySuccess: state.messaging.messageSummarySuccess,
  summarizedMessageAndTimeline: state.messaging.summarizedMessageAndTimeline,
  phoneNumber: state.messaging.phoneNumber,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    SummarizeMessage,
  })(ChatAssistant)
);
